import AdminDropdownCard from '@components/AdminDropdownCard/AdminDropdownCard.component'
import ReusableModal from '@components/Modal/ReusableModal.component'
import { Box, Button, Divider, FormLabel, MenuItem, styled, TextareaAutosize, TextField, Typography, Radio, RadioGroup, FormControlLabel, Card, CardContent, Chip } from '@mui/material'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AdminPageHeader from '@components/AdminPageHeader/AdminPageHeader.component'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Route as AdminDashboardRoute } from './_admin.admin-dashboard';


export const Route = createFileRoute('/(admin)/_admin/create-category-old')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      technologyId: search.technologyId as string | undefined,
      categoryId: search.categoryId as string | undefined,
    }
  },
  component: RouteComponent,
})

const StyledTextArea = styled(TextareaAutosize)({
    backgroundColor: '#F9F9F9',
    width: '100%',
    minHeight: '135px',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid var(--Interviewer-Lightest-Black)',
    fontFamily: 'inherit',
    fontSize: '14px',
    lineHeight: '20px',
    resize: 'vertical',
    '&:focus': {
    outline: 'none',
    },
})

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { technologyId, categoryId } = useSearch({ from: '/(admin)/_admin/create-category' });
  const [openModal, setOpenModal] = useState(false)
  const handleClose = () => setOpenModal(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [createdCategoryId, setCreatedCategoryId] = useState<string | null>(null);
  
  // Question form state
  const [questionText, setQuestionText] = useState('');
  const [questionStructure, setQuestionStructure] = useState<number>(2); // Default to OneSelect
  const [answers, setAnswers] = useState([
    { answer: '', correct: true },
    { answer: '', correct: false }
  ]);
  const [textualExplanation, setTextualExplanation] = useState('');
  const [editingQuestion, setEditingQuestion] = useState<any>(null);

  // Fetch questions for the category (when creating new category)
  const { data: questions, isLoading: isLoadingQuestions, error: questionsError } = useQuery({
    queryKey: ['category-questions', createdCategoryId],
    queryFn: async () => {
      if (!createdCategoryId) return [];
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://interviewer.runasp.net/api/categories/${createdCategoryId}/questions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: !!createdCategoryId,
  });

  // Fetch questions for existing category (when editing)
  const { data: existingQuestions, isLoading: isLoadingExistingQuestions, error: existingQuestionsError } = useQuery({
    queryKey: ['category-questions', categoryId],
    queryFn: async () => {
      if (!categoryId) return [];
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://interviewer.runasp.net/api/categories/${categoryId}/questions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: !!categoryId,
  });

  const linkCategoryMutation = useMutation({
    mutationFn: async ({ technologyId, categoryId }: { technologyId: string; categoryId: string }) => {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `https://interviewer.runasp.net/api/technologies/${technologyId}/link-category`,
        { categoryId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      console.log('Category linked successfully!', data);
      // Navigate back to the technology page if we came from there
      if (technologyId) {
        navigate({ to: `/technology/${technologyId}` });
      }
    },
    onError: (error) => {
      console.error('Error linking category:', error);
    },
  });

  const createCategoryMutation = useMutation({
    mutationFn: async (categoryData: { name: string; description: string }) => {
      const token = localStorage.getItem('token');
      const response = await axios.post('https://interviewer.runasp.net/api/categories', categoryData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    },
    onSuccess: (data) => {
      console.log('Category created successfully! Category ID:', data);
      const categoryId = data.id || data;
      setCreatedCategoryId(categoryId); // Store the category ID
      setName('');
      setDescription('');
      
      // If we have a technologyId, link the category to the technology
      if (technologyId) {
        linkCategoryMutation.mutate({ technologyId, categoryId });
      }
    },
    onError: (error) => {
      console.error('Error creating category:', error);
    },
  });

  const createQuestionMutation = useMutation({
    mutationFn: async (questionData: { text: string; structure: number; suggestedAnswers?: Array<{ answer: string; correct: boolean }> }) => {
      const targetCategoryId = categoryId || createdCategoryId;
      if (!targetCategoryId) {
        throw new Error('No category ID available');
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`https://interviewer.runasp.net/api/categories/${targetCategoryId}/questions`, questionData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    },
    onSuccess: (data) => {
      console.log('Question created successfully!', data);
      // Reset form
      setQuestionText('');
      setQuestionStructure(2);
      setAnswers([
        { answer: '', correct: true },
        { answer: '', correct: false }
      ]);
      handleClose();
      // Refetch questions to show the new one
      const targetCategoryId = categoryId || createdCategoryId;
      queryClient.invalidateQueries({ queryKey: ['category-questions', targetCategoryId] });
    },
    onError: (error) => {
      console.error('Error creating question:', error);
    },
  });

  const handleSubmit = () => {
    if (!name.trim() || !description.trim()) {
      console.log('Please fill in both name and description fields.');
      return;
    }
    
    createCategoryMutation.mutate({ name: name.trim(), description: description.trim() });
  };

  const handleEditQuestion = (question: any) => {
    setEditingQuestion(question);
    setQuestionText(question.text);
    setQuestionStructure(question.structure);
    setAnswers(question.suggestedAnswers || []);
    setOpenModal(true);
  };

  const handleAddQuestion = () => {
    if (!questionText.trim()) {
      console.log('Please fill in the question text.');
      return;
    }

    if (editingQuestion) {
      console.log(editingQuestion.id);
      
      // Update existing question
      const token = localStorage.getItem('token');
      axios.put(
        `https://interviewer.runasp.net/api/questions/${editingQuestion.id}`,
        {
          text: questionText.trim(),
          SuggestedAnswers: answers,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      ).then(() => {
        setEditingQuestion(null);
        setOpenModal(false);
        queryClient.invalidateQueries({ queryKey: ['category-questions', categoryId || createdCategoryId] });
      });
      return;
    }

    if (questionStructure === 1) {
      // Textual
      if (!answers[0].answer.trim()) {
        console.log('Please fill in the answer.');
        return;
      }
      createQuestionMutation.mutate({
        text: questionText.trim(),
        structure: 1,
        // Do NOT send suggestedAnswers for textual
        // explanation: textualExplanation // Uncomment if backend supports this
      });
    } else {
      // One/Multiple
      const validAnswers = answers.filter(a => a.answer.trim() !== '');
      if (validAnswers.length < 2) {
        console.log('Please add at least 2 answers.');
        return;
      }
      const hasCorrectAnswer = validAnswers.some(a => a.correct);
      if (!hasCorrectAnswer) {
        console.log('Please mark at least one answer as correct.');
        return;
      }
      createQuestionMutation.mutate({
        text: questionText.trim(),
        structure: questionStructure,
        suggestedAnswers: validAnswers
      });
    }
  };

  const handleAddAnswer = () => {
    setAnswers([...answers, { answer: '', correct: false }]);
  };

  const handleAnswerChange = (index: number, field: 'answer' | 'correct', value: string | boolean) => {
    const newAnswers = [...answers];
    newAnswers[index] = { ...newAnswers[index], [field]: value };
    setAnswers(newAnswers);
  };

  const handleRemoveAnswer = (index: number) => {
    if (answers.length > 2) {
      const newAnswers = answers.filter((_, i) => i !== index);
      setAnswers(newAnswers);
    }
  };

  const handleStructureChange = (value: number) => {
    setQuestionStructure(value);
    // Reset correct answers based on structure type
    if (value === 2) { // OneSelect - only one correct answer
      setAnswers(answers.map((answer, index) => ({ ...answer, correct: index === 0 })));
    }
  };

  const getStructureLabel = (structure: number) => {
    switch (structure) {
      case 1: return 'Text';
      case 2: return 'One Select';
      case 3: return 'Multiple Choice';
      default: return 'Unknown';
    }
  };

  return <>
    <Box>
      <AdminPageHeader title="New Category"
        icon={<ArrowBackIcon color="primary" fontSize="medium" />} 
        onIconClick={() => navigate({ to: AdminDashboardRoute.to })}
      />
      <Box sx={{
          px: {
            xs: '16px',
            md: '32px'    
          },
          py: {
            xs: '16px',
            md: '32px'
          }
        }}>
        {/* <TextField 
            label="Select a technology"
            variant="outlined"
            select
            sx={{width: '50%'}}
        >
            <MenuItem>Test menu Angular</MenuItem>
        </TextField> */}
        <Box sx={{display: 'flex', flexDirection: 'column', gap: {xs: '20px', md: '48px'}}}>
          <TextField 
                label="Category Name"
                variant="outlined"
                fullWidth
                value={name}
                onChange={e => setName(e.target.value)}
          />
          <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
            <FormLabel sx={{fontSize: '16px', fontWeight: '500', color: 'var(--Interviewer-Black)'}}>Short Category Description</FormLabel> 
            <StyledTextArea 
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end'}}>
            <Button 
              variant="contained" 
              onClick={handleSubmit}
              disabled={createCategoryMutation.isPending || linkCategoryMutation.isPending}
              sx={{ minWidth: '120px' }}
            >
              {createCategoryMutation.isPending ? 'Creating...' : 'Create Category'}
            </Button>
          </Box>
        </Box>
        
        {(createdCategoryId || categoryId) && (
          <>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: '48px'}}>
                <Typography variant='h2'>All questions</Typography>
                <Button onClick={() => setOpenModal(true)} variant='contained'>Add a question</Button>
            </Box>
            <Divider 
              orientation="horizontal" 
              sx={{ borderColor: 'var(--Interviewer-Near-White)', pt: '16px'}}
            />

            {/* Display Questions */}
            <Box sx={{ pt: '24px' }}>
              {((questions && questions.length > 0) ? questions : (existingQuestions && existingQuestions.length > 0 ? existingQuestions : [])).map((question: any, idx: number) => (
                <AdminDropdownCard
                  key={question.id || idx}
                  question={question.text}
                  structure={question.structure}
                  suggestedAnswers={question.suggestedAnswers || []}
                  index={idx}
                  questionId={question.id}
                  onEdit={() => handleEditQuestion(question)}
                />
              ))}
            </Box>

            <Box sx={{display: 'flex', mx: 'auto' ,pt: '24px', maxWidth: '1024px'}}>
                {/* <AdminDropdownCard /> */}
            </Box>
          </>
        )}

        <ReusableModal open={openModal} onClose={() => { setOpenModal(false); setEditingQuestion(null); }}>
            <Typography variant='h2' sx={{ pb: '37px' }}>Add a question</Typography>
            <Typography sx={{ fontWeight: 500, pb: '8px' }}>Question Text</Typography>
            <TextField
              multiline
              minRows={4}
              placeholder="Add a question text here"
              fullWidth
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: '1px solid var(--Interviewer-Lightest-Black)',
                    borderRadius: '12px',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--Interviewer-Lightest-Black)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--Interviewer-Lightest-Black)',
                  },
                },
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, pt:'37px', pb: '30px', color: 'var(--Interviewer-Black)' }}>
              <Typography sx={{ fontWeight: 500, color: 'var(--Interviewer-Black)' }}>Choose answer type</Typography>
              <RadioGroup
                value={questionStructure}
                onChange={(e) => handleStructureChange(Number(e.target.value))}
                row
              >
                <FormControlLabel 
                  value={2} 
                  control={<Radio />} 
                  label="One answer" 
                  sx={{ color: 'var(--Interviewer-Black)' }}
                />
                <FormControlLabel 
                  value={3} 
                  control={<Radio />} 
                  label="Multiple Choice" 
                  sx={{ color: 'var(--Interviewer-Black)' }}
                />
                <FormControlLabel 
                  value={1} 
                  control={<Radio />} 
                  label="Textual" 
                  sx={{ color: 'var(--Interviewer-Black)' }}
                />
              </RadioGroup>
            </Box>

            <Divider sx={{ mb: '30px', borderColor: 'var(--Interviewer-Near-White)' }} />

            {questionStructure === 1 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', pb: '24px' }}>
                <Box>
                  <Typography sx={{ fontWeight: 500, fontSize: '16px', color: 'var(--Interviewer-Black)', mb: 1 }}>
                    Answer
                  </Typography>
                  <TextField
                    placeholder="Add answer text here"
                    fullWidth
                    variant="outlined"
                    value={answers[0]?.answer || ''}
                    onChange={e => handleAnswerChange(0, 'answer', e.target.value)}
                    multiline
                    minRows={4}
                  />
                </Box>
                {/* <Box>
                  <Typography sx={{ fontWeight: 500, fontSize: '16px', color: 'var(--Interviewer-Black)', mb: 1 }}>
                    Explanation (optional)
                  </Typography>
                  <TextField
                    placeholder="Add explanation or notes here"
                    fullWidth
                    variant="outlined"
                    value={textualExplanation}
                    onChange={e => setTextualExplanation(e.target.value)}
                    multiline
                    minRows={4}
                  />
                </Box> */}
              </Box>
            ) : (
              <>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: '24px', pb: '24px'}}>
                  {answers.map((answer, index) => (
                    <Box key={index}>
                      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px'}}> 
                        <Typography sx={{ fontWeight: 500, fontSize: '16px', color: 'var(--Interviewer-Black)' }}>
                          Answer {index + 1}
                        </Typography>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <CheckCircleIcon 
                            fontSize="small" 
                            sx={{ 
                              color: answer.correct ? 'var(--Interviewer-Green)' : 'var(--Interviewer-Lightest-Black)',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              if (questionStructure === 2) { // OneSelect - only one correct
                                setAnswers(answers.map((a, i) => ({ ...a, correct: i === index })));
                              } else { // MultiSelect - toggle
                                handleAnswerChange(index, 'correct', !answer.correct);
                              }
                            }}
                          />
                          <Typography sx={{ fontSize: 14, color: 'var(--Interviewer-Black)' }}>
                            Mark as correct
                          </Typography>
                          {answers.length > 2 && (
                            <Button 
                              size="small" 
                              onClick={() => handleRemoveAnswer(index)}
                              sx={{ ml: 1, minWidth: 'auto', p: 0.5 }}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          placeholder={`Add answer ${index + 1} text here`}
                          fullWidth
                          variant="outlined"
                          value={answer.answer}
                          onChange={(e) => handleAnswerChange(index, 'answer', e.target.value)}
                          multiline={questionStructure === 1}
                          minRows={questionStructure === 1 ? 4 : 1}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ mb: '32px', p: '24px', border: '1px dashed var(--Interviewer-Near-White)', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '30px', cursor: 'pointer' }} onClick={handleAddAnswer}>
                  <Box sx={{background: '#fff', boxShadow: '0px 0.75px 1.5px 0px #0205111A', p: '10px', borderRadius: '10px'}}>
                    <AddOutlinedIcon color="primary" fontSize='small'/>
                  </Box>
                  <Typography>Add another choice</Typography>
                </Box>
              </>
            )}

            <Button 
              variant="contained" 
              fullWidth 
              onClick={handleAddQuestion}
              disabled={createQuestionMutation.isPending}
            >
              {editingQuestion ? 'Update question' : 'Add a question'}
            </Button>
        </ReusableModal>
      </Box>
    </Box>
  </>
}
